import { PaymentTermCountKind } from '@/common/enums/PaymentTermCountKind';
import { TransportKind } from '@/common/enums/TransportKind';

/** Own-transport kind returned by offer list API (`transport.kind`). */
const OWN_TRANSPORT_KIND = 'O';

/**
 * Whether transport fee should be included in the percentage promotion base.
 * Includes own transport and plane offers; regular coach transport stays outside promo.
 *
 * @param transportKind - Offer/term transport kind (`C` / `F` / `W` / `O`).
 * @param isOwnTransport - Whether the participant currently uses own transport (no stop).
 * @returns True when transport belongs in the promotable base.
 */
export function shouldIncludeTransportInPromo(transportKind: string, isOwnTransport: boolean): boolean {
	const isPlaneOffer = transportKind === TransportKind.FLIGHT;
	const isOwnTransportKind = transportKind === OWN_TRANSPORT_KIND || transportKind === TransportKind.WITHOUT;

	return isOwnTransport || isOwnTransportKind || isPlaneOffer;
}

/**
 * Calculates percentage promotion amount from a promotable base.
 * `methodValue` uses permille (`100` = 10%).
 *
 * @param promotableBase - Price base subject to percentage discount.
 * @param methodValue - Promotion method value in permille.
 * @returns Discount amount in grosze.
 */
export function calcPercentagePromotionValue(promotableBase: number, methodValue: number): number {
	return (promotableBase * methodValue) / 1000;
}

/**
 * Adjusts API offer full price so plane/own-transport percentage promos
 * include transport in the base. Backend mat view always applies promo to
 * `(catalog - fee)` then adds fee back.
 *
 * @param fullPrice - Full price from offer list API (after backend promo).
 * @param stopFee - Transport stop fee from the offer.
 * @param transportKind - Offer/term transport kind.
 * @param promotion - Selected promotion or null.
 * @returns Adjusted full price matching WWW calcPrice for plane/own transport.
 */
export function adjustFullPriceForTransportInPromo(
	fullPrice: number,
	stopFee: number,
	transportKind: string,
	promotion: { method: string; methodValue: number } | null | undefined,
): number {
	if (!promotion || promotion.method !== PaymentTermCountKind.PERCENTAGE) {
		return fullPrice;
	}

	if (!shouldIncludeTransportInPromo(transportKind, false)) {
		return fullPrice;
	}

	return fullPrice - calcPercentagePromotionValue(stopFee, promotion.methodValue);
}
